import '/ci_source/styles/darkly.scss';

import '@fortawesome/fontawesome-free/css/fontawesome.css';
import '@fortawesome/fontawesome-free/css/solid.css';

import 'bootstrap';
import React, { useState } from 'react';

import { displayNotification, fetchPost, logError } from './utility';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useMutation } from 'react-query';
import { Button, Modal, Password } from './components';
import LoginAlerts from './components/LoginAlerts';
import { ORG_LOGO_BRIGHT } from './constants';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: false,
			staleTime: Infinity,
		},
	},
});

const serverVars = JSON.parse(window.atob(document.getElementById('loginRoot')!.dataset.vars!));

const Login = () => {

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');

	const cooldownSeconds = 120;
	const [forgotPasswordModal, setForgotPasswordModal] = useState(false);
	const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
	const [forgotPasswordCooldown, setForgotPasswordCooldown] = useState(false);
    const [forgotPasswordCooldownTime, setForgotPasswordCooldownTime] = useState(cooldownSeconds);

	const [downtime, setDowntime] = useState(false);
	const [warning, setWarning] = useState(false);
	const [warningMsg, setWarningMsg] = useState('The EEO Training system is currently experiencing operational difficulties. This may impact usability for some time until it is resolved.');
	const [warningStart, setWarningStart] = useState('');
	const [scheduled, setScheduled] = useState(false);
	const [scheduledStart, setScheduledStart] = useState('');
	const [scheduledEnd, setScheduledEnd] = useState('');
	const [scheduledStartDay, setScheduledStartDay] = useState('');
	const [scheduledEndDay, setScheduledEndDay] = useState('');
	const [overtime, setOvertime] = useState(true);

	const status = fetchPost('/internal_login/system_status').then(({ downtimeInfo, scheduledInfo, warningReport, overtime }) => {
		setOvertime(overtime);
		if (downtimeInfo.length > 0) { 
			downtimeInfo = downtimeInfo[0];
			setDowntime(true);
			setScheduledEnd(downtimeInfo['dt_scheduled_end_time_formatted_time'] ? downtimeInfo['dt_scheduled_end_time_formatted_time'] : '');
			setScheduledEndDay(downtimeInfo['dt_scheduled_end_time_formatted_weekday'] ? downtimeInfo['dt_scheduled_end_time_formatted_weekday'] : '');
		}
		if (scheduledInfo.length > 0) { 
			setScheduled(true);
			scheduledInfo = scheduledInfo[0];
			setScheduledStart(scheduledInfo['dt_scheduled_start_time_formatted_time'] ? scheduledInfo['dt_scheduled_start_time_formatted_time'] : '');
			setScheduledEnd(scheduledInfo['dt_scheduled_end_time_formatted_time'] ? scheduledInfo['dt_scheduled_end_time_formatted_time'] : '');
			setScheduledStartDay(scheduledInfo['dt_scheduled_start_time_formatted_weekday'] ? scheduledInfo['dt_scheduled_start_time_formatted_weekday'] : '');
			setScheduledEndDay(scheduledInfo['dt_scheduled_end_time_formatted_weekday'] ? scheduledInfo['dt_scheduled_end_time_formatted_weekday'] : '');
		}
		if (warningReport.length > 0) {
			warningReport = warningReport[0];
			setWarning(true);
			let warningNote = warningReport['dt_notes'];
			if (warningNote && warningNote != '') setWarningMsg(warningNote);
			setWarningStart(warningReport['warning_start_time_formatted']);
		}
	});

	const loginSubmit = useMutation({
		mutationFn: async (event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const postData = {
				email: email.trim(),
				password: password.trim(),
			};
			return await fetchPost('/internal_login/dashboard_login', postData);
		},
		onSuccess: ({ guid }) => {
			window.location.replace(`/ui/dashboard/${guid}`);
		},
		onError: (error) => {
			switch (error) {
				case 'Timeout':
					displayNotification('Please try again in 5 minutes', 'danger');
					break;
				default:
					displayNotification('Login failed', 'danger');
			}
		},
	});

	const forgotPasswordSubmit = useMutation({
		mutationFn: async (event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const postData = {
				email: forgotPasswordEmail.trim(),
			};
			return await fetchPost('/internal_login/forgot_password', postData);
		},
		onSuccess: ({ email }) => {
			displayNotification(`Email sent to ${email}`);
			setForgotPasswordModal(false);
			$('#loginForgotPasswordModal').modal('hide');
			startForgotPasswordCooldown();
		},
		onError: (error: any) => {
			switch (error.msg) {
				case 'NOT_FOUND':
					displayNotification('No account found under this email address', 'danger');
					break;
				default:
					displayNotification('An error occured while sending a temporary password', 'danger');
					logError(error);
					break;
			}
		},
	});

	// Forgot Password button cooldown to prevent request spamming
	let timeLeft = cooldownSeconds * 1000;
    const startForgotPasswordCooldown = () => {
        setForgotPasswordCooldown(true);
        
        const intervalId = setInterval(() => {
                timeLeft -= 1000;
                setForgotPasswordCooldownTime(Math.floor(timeLeft / 1000));
                if (timeLeft <= 0) {
                clearInterval(intervalId);
                setForgotPasswordCooldown(false);
            }
        }, 1000);
    };

	return (
		<>
			<LoginAlerts
				system="dashboard"
				downtime={downtime}
				warning={warning}
				warningMsg={warningMsg}
				warningStart={warningStart}
				scheduled={scheduled}
				scheduledStart={scheduledStart}
				scheduledEnd={scheduledEnd}
				scheduledStartDay={scheduledStartDay}
				scheduledEndDay={scheduledEndDay}
				overtime={overtime}
			/>

			<div hidden={downtime} className={`card border-0 shadow-sm ${serverVars.org_logo_theme == ORG_LOGO_BRIGHT ? 'bg-dark' : 'bg-secondary'}`} 
				style={{ width: '100%', maxWidth: '450px', margin: '0 auto', borderRadius: '10px'}}>
				<div className="card-body p-4 p-md-5">
					<div className="text-center mb-4">
						<img src={serverVars.logourl} className='mb-4' style={{ width: 240, height: 'auto' }} />
						<h4>Admin Dashboard Login</h4>
					</div>
					<form onSubmit={loginSubmit.mutate}>
						<div className="mb-3">
							<label>Email / Company</label>
							<input type="text" required maxLength={100} className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
						</div>
						<div className="mb-4">
							<label>Password</label>
							<Password required maxLength={100} value={password} onChange={(e) => setPassword(e.target.value)} />
						</div>
						<Button type="submit" disabled={downtime} className="btn btn-primary w-100" spinner={loginSubmit.isLoading}>
							<i className="fas fa-right-to-bracket me-2" />Log in
						</Button>
						<div className="text-center mt-3">
							<button type="button" disabled={downtime || forgotPasswordCooldown} data-bs-toggle="modal" data-bs-target="#loginForgotPasswordModal" className="btn btn-link text-decoration-none">
								{forgotPasswordCooldown ? (
									<><i className="fas fa-hourglass-half me-2" />Please wait... ({forgotPasswordCooldownTime}s)</>
								) : (
									<>Forgot Password?</>
								)}
							</button>
						</div>
					</form>
				</div>
			</div>

			{/* Forgot Password Modal */}
			<Modal id="loginForgotPasswordModal" title="Forgot Password" show={forgotPasswordModal} onHide={() => setForgotPasswordModal(false)} onHidden={() => setForgotPasswordEmail('')}>
				<div className="modal-body">
					<form id="loginForgotPasswordForm" onSubmit={forgotPasswordSubmit.mutate}>
						<label>Enter your email address associated with the account:</label>
						<input type="email" className="form-control" required value={forgotPasswordEmail} onChange={(e) => setForgotPasswordEmail(e.target.value)} />
						<small style={{color: 'gray'}}><i>We will email a temporary password to this address, which can be used to log in and set a new password for future use.</i></small>
					</form>
				</div>
				<div className="modal-footer">
					<button type="button" className="btn btn-secondary" data-bs-dismiss="modal"><i className="fas fa-xmark me-2" />Cancel</button>
					<Button type="submit" form="loginForgotPasswordForm" className="btn btn-primary" spinner={forgotPasswordSubmit.isLoading}><i className="fas fa-envelope me-2" />Submit</Button>
				</div>
			</Modal>
		</>
	);
};

const root = createRoot(document.getElementById('loginRoot')!);
root.render(
	<QueryClientProvider client={queryClient}>
		<Login />
	</QueryClientProvider>
);

$('#loginSpinner').fadeOut(500, () => $('#loginSpinner').remove());